// src/components/Gallery.jsx
//
// Completed work, fetched from the API and managed in the portal.
//
// Unlike the page copy, there is no local fallback: an empty gallery is a
// truthful state and a hardcoded one would be a lie the moment Brooke edits
// it. So the section renders nothing at all until there is something to show,
// and disappears entirely if the API is unreachable.
//
// The rail is a native scroll-snap container, so mobile gets a real swipe
// with no JavaScript and desktop gets arrow controls. Scrolling is the
// browser's, not ours, which is why it stays smooth on a phone.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "motion/react";
import { ArrowLeft, ArrowRight } from "lucide-react";

const API = process.env.REACT_APP_API_URL;
const MEDIA = process.env.REACT_APP_MEDIA_URL;
const SITE = "cold_storage";

export default function Gallery() {
  const railRef = useRef(null);
  const [items, setItems] = useState(null);
  const [edges, setEdges] = useState({ start: true, end: false });

  useEffect(() => {
    let alive = true;

       // no-cache: the browser still revalidates, so a photo published in the
    // portal shows on a normal refresh rather than needing a hard one
    fetch(`${API}/v1/gallery/${SITE}`, { cache: "no-cache" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (alive) setItems(d?.items || []);
      })
      .catch(() => {
        if (alive) setItems([]);
      });

    return () => {
      alive = false;
    };
  }, []);

  const readEdges = useCallback(() => {
    const el = railRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setEdges({ start: el.scrollLeft <= 4, end: el.scrollLeft >= max - 4 });
  }, []);

  useEffect(() => {
    readEdges();
    const el = railRef.current;
    if (!el) return;
    el.addEventListener("scroll", readEdges, { passive: true });
    window.addEventListener("resize", readEdges);
    return () => {
      el.removeEventListener("scroll", readEdges);
      window.removeEventListener("resize", readEdges);
    };
  }, [readEdges, items]);

  const nudge = (dir) => {
    const el = railRef.current;
    if (!el) return;
    // One card plus its gap, so a click lands cleanly on the next snap point.
    const card = el.querySelector("[data-card]");
    const step = card ? card.offsetWidth + 20 : el.clientWidth * 0.8;
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  // Nothing published, still loading, or the API is down. All three are the
  // same to a visitor: there is no section.
  if (!items || items.length === 0) return null;

  return (
    <section id="work" className="border-t border-line py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="flex flex-wrap items-end justify-between gap-6"
        >
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-brooke-red">
              Recent work
            </p>
            <h2 className="mt-3 max-w-[18ch] font-arch text-[clamp(26px,4.4vw,42px)] font-extrabold leading-[1.08] tracking-[-0.022em] text-ink">
              Installations on the ground
            </h2>
          </div>

          {items.length > 1 && (
            <div className="hidden gap-2 md:flex">
              <RailButton
                dir={-1}
                disabled={edges.start}
                onClick={() => nudge(-1)}
                label="Previous"
              />
              <RailButton
                dir={1}
                disabled={edges.end}
                onClick={() => nudge(1)}
                label="Next"
              />
            </div>
          )}
        </motion.div>
      </div>

      {/* Inside the container rather than full bleed. Escaping the container
          looked better on a phone but depended on no ancestor clipping
          horizontal overflow, and .bk-page does exactly that. */}
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div
          ref={railRef}
          className="mt-10 flex snap-x snap-mandatory gap-5 overflow-x-auto scroll-smooth pb-4 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {items.map((job, i) => (
            <motion.article
            key={job.id}
            data-card
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
            className="group w-[min(80vw,340px)] shrink-0 snap-start overflow-hidden rounded-2xl border border-line bg-surface2 sm:w-[360px]"
          >
            <JobImage job={job} />

            <div className="p-6">
              <div className="flex items-center justify-between gap-3">
                {job.sector && (
                  <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-brooke-red">
                    {job.sector}
                  </span>
                )}
                {job.location && (
                  <span className="ml-auto font-mono text-[9px] uppercase tracking-[0.16em] text-subtle">
                    {job.location}
                  </span>
                )}
              </div>

              <h3 className="mt-3 font-arch text-lg font-extrabold leading-snug text-ink">
                {job.title}
              </h3>

              {job.body && (
                <p className="mt-2.5 text-[13.5px] leading-relaxed text-muted">
                  {job.body}
                </p>
              )}

              {Array.isArray(job.spec) && job.spec.length > 0 && (
                <dl className="mt-5 border-t border-line">
                  {job.spec.map((row, n) => (
                    <div
                      key={n}
                      className="flex items-baseline justify-between gap-4 border-b border-line py-2.5"
                    >
                      <dt className="font-mono text-[9px] uppercase tracking-[0.16em] text-subtle">
                        {row.label}
                      </dt>
                      <dd className="text-right font-mono text-[11px] tracking-[0.03em] text-muted">
                        {row.value}
                      </dd>
                    </div>
                  ))}
                </dl>
              )}
            </div>
          </motion.article>
        ))}

        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Renders the photograph when there is one, and an intentional panel when
// there is not, so an entry published before its photo never shows as broken.
function JobImage({ job }) {
  const [failed, setFailed] = useState(false);
  const show = job.image_path && !failed;

  return (
    <div className="relative aspect-[4/3] overflow-hidden border-b border-line bg-bg">
      {show ? (
        <img
          src={`${MEDIA}${job.image_path}`}
          alt={job.image_alt || job.title}
          loading="lazy"
          decoding="async"
          onError={() => setFailed(true)}
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
        />
           ) : (
        // Shown when an entry is published before its photograph is uploaded.
        <img
          src={`${process.env.PUBLIC_URL}/gallery.png`}
          alt=""
          loading="lazy"
          className="h-full w-full object-cover"
        />
      )}

      {job.reference && (
        <span className="absolute left-4 top-4 rounded-full border border-line bg-bg/80 px-3 py-1 font-mono text-[9px] uppercase tracking-[0.16em] text-muted backdrop-blur">
          {job.reference}
        </span>
      )}
    </div>
  );
}

function RailButton({ dir, disabled, onClick, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className="grid h-10 w-10 place-items-center rounded-full border border-line text-muted transition hover:border-brooke-red hover:text-ink disabled:pointer-events-none disabled:opacity-30"
    >
      {dir < 0 ? <ArrowLeft size={16} /> : <ArrowRight size={16} />}
    </button>
  );
}